import { dayShort, fromIso, isIsoDate, iso, monthShort, monthStart, quarterStart, startOfWeek, weekNumber } from '../../core/format/dates'
import type { SalesGroupBy } from '../../core/types/adminRequests'

/** De periodes op het omzetscherm. Alles "tot en met vandaag", zodat de vergelijking eerlijk is. */
export const periods = [
  { key: 'vandaag', label: 'Vandaag' },
  { key: 'week', label: 'Week' },
  { key: 'maand', label: 'Maand' },
  { key: 'kwartaal', label: 'Kwartaal' },
  { key: 'jaar', label: 'Jaar' },
  { key: 'zelf', label: 'Zelf kiezen' },
] as const

export type PeriodKey = (typeof periods)[number]['key']

export const asPeriod = (v: string | null): PeriodKey => periods.find((p) => p.key === v)?.key ?? 'maand'

/** Van–tot voor een periode. Bij "zelf kiezen" de gekozen datums, of anders deze maand. */
export function rangeFor(key: PeriodKey, today: string, custom?: { from: string | null; to: string | null }): { from: string; to: string } {
  switch (key) {
    case 'vandaag':
      return { from: today, to: today }
    case 'week':
      return { from: iso(startOfWeek(fromIso(today))), to: today }
    case 'kwartaal':
      return { from: quarterStart(today), to: today }
    case 'jaar':
      return { from: `${today.slice(0, 4)}-01-01`, to: today }
    case 'zelf': {
      const from = custom?.from
      const to = custom?.to
      if (isIsoDate(from) && isIsoDate(to) && from <= to) return { from, to }
      return { from: monthStart(today), to: today }
    }
    default:
      return { from: monthStart(today), to: today }
  }
}

/** "1 – 3 okt" of "3 okt" als het één dag is. */
export const rangeLabel = (from: string, to: string) => (from === to ? dayShort(from) : `${dayShort(from)} – ${dayShort(to)}`)

/** Het label onder een staaf in de grafiek. */
export const bucketLabel = (date: string, groupBy: SalesGroupBy) =>
  groupBy === 'day' ? String(Number(date.slice(8, 10))) : groupBy === 'week' ? `wk ${weekNumber(fromIso(date))}` : monthShort(date).split(' ')[0]

/** Het verschil met de vorige periode, als percentage. `null` als er toen niets was. */
export function change(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null
  return Math.round(((current - previous) / Math.abs(previous)) * 100)
}

/** "+12%" of "−4%" (met een echt minteken). */
export const changeLabel = (pct: number) => `${pct > 0 ? '+' : pct < 0 ? '−' : ''}${Math.abs(pct)}%`
